import { query } from "../erd-editor-schema/dist/query/index.js";
import { Show } from "../constants/schema.js";
import { arrayHas } from "./arrayHas.js";
import { bHas } from "./bit.js";
import { getTableRect, unionRect } from "../konva/scene/metrics.js";
import { toOpaqueHex } from "./tableColor.js";
import { contrastTextColor } from "./contrastText.js";
//#region src/utils/tableGroup.ts
function getTableGroupId({ doc }, table) {
	return table.groupId && doc.tableGroupIds.includes(table.groupId) ? table.groupId : "";
}
function getMemberTables(state, groupId) {
	const { doc, collections } = state;
	if (!groupId || !doc.tableGroupIds.includes(groupId)) return [];
	return query(collections).collection("tableEntities").selectByIds(doc.tableIds).filter((table) => table.groupId === groupId);
}
function getTableGroupMemberIds(state, groupId) {
	return getMemberTables(state, groupId).map(({ id }) => id);
}
function padRect(rect, padding = 24) {
	const top = padding + 28;
	return {
		x: rect.x - padding,
		y: rect.y - top,
		width: rect.width + padding * 2,
		height: rect.height + padding + top
	};
}
function getTableGroupRect(state, group, { excludeTableIds = [], members = getMemberTables(state, group.id) } = {}) {
	const { x, y, width, height } = group.ui;
	const excluded = new Set(excludeTableIds);
	return members.filter((table) => !excluded.has(table.id)).reduce((rect, table) => unionRect(rect, padRect(getTableRect(state, table))), {
		x,
		y,
		width,
		height
	});
}
function seedTableGroupBoxes(groups) {
	return new Map(groups.map(({ id, ui: { x, y, width, height } }) => [id, {
		x,
		y,
		width,
		height
	}]));
}
function growTableGroupBox(boxes, table, rect) {
	const box = table.groupId ? boxes.get(table.groupId) : void 0;
	if (box) boxes.set(table.groupId, unionRect(box, padRect(rect)));
}
function getTableGroupRects(state, { excludeTableIds = [] } = {}) {
	const { doc, collections } = state;
	const boxes = seedTableGroupBoxes(query(collections).collection("tableGroupEntities").selectByIds(doc.tableGroupIds));
	if (!boxes.size) return boxes;
	const excluded = new Set(excludeTableIds);
	query(collections).collection("tableEntities").selectByIds(doc.tableIds).forEach((table) => {
		if (excluded.has(table.id)) return;
		growTableGroupBox(boxes, table, getTableRect(state, table));
	});
	return boxes;
}
function getTableGroupMembers(state) {
	const { doc, collections } = state;
	const listed = new Set(doc.tableGroupIds);
	const members = /* @__PURE__ */ new Map();
	if (!listed.size) return members;
	query(collections).collection("tableEntities").selectByIds(doc.tableIds).forEach((table) => {
		if (!listed.has(table.groupId)) return;
		const list = members.get(table.groupId);
		list ? list.push(table) : members.set(table.groupId, [table]);
	});
	return members;
}
function getTablesGroupRect(state, tableIds) {
	const { doc, collections } = state;
	const rects = query(collections).collection("tableEntities").selectByIds(tableIds.filter(arrayHas(doc.tableIds))).map((table) => getTableRect(state, table));
	return rects.length ? padRect(rects.reduce(unionRect)) : null;
}
function getTableGroupWraps(state, points = []) {
	const { doc, collections } = state;
	const placed = new Map(points.map((point) => [point.id, point]));
	const rectsByGroup = /* @__PURE__ */ new Map();
	query(collections).collection("tableEntities").selectByIds(doc.tableIds).forEach((table) => {
		const groupId = getTableGroupId(state, table);
		if (!groupId) return;
		const { x, y } = placed.get(table.id) ?? table.ui;
		const rect = {
			...getTableRect(state, table),
			x,
			y
		};
		const rects = rectsByGroup.get(groupId);
		rects ? rects.push(rect) : rectsByGroup.set(groupId, [rect]);
	});
	return doc.tableGroupIds.flatMap((id) => {
		const rects = rectsByGroup.get(id);
		return rects ? [{
			...padRect(rects.reduce(unionRect)),
			id
		}] : [];
	});
}
function getTableCenter(state, table) {
	const { x, y, width, height } = getTableRect(state, table);
	return {
		x: x + width / 2,
		y: y + height / 2
	};
}
function isPointInRect({ x, y }, rect) {
	return rect.x <= x && x <= rect.x + rect.width && rect.y <= y && y <= rect.y + rect.height;
}
function findTableGroupsAt(state, points, { boxes, ...options } = {}) {
	if (!points.length) return [];
	const { doc, collections } = state;
	const rects = boxes ?? getTableGroupRects(state, options);
	const candidates = query(collections).collection("tableGroupEntities").selectByIds(doc.tableGroupIds).flatMap((group) => {
		const rect = rects.get(group.id);
		return rect ? [{
			group,
			rect
		}] : [];
	});
	return points.map((point) => candidates.reduce((top, { group, rect }) => (!top || top.ui.zIndex <= group.ui.zIndex) && isPointInRect(point, rect) ? group : top, null));
}
var nextTableGroupZIndex = (groups) => Math.max(0, ...groups.map(({ ui }) => ui.zIndex)) + 1;
var isTableGroupShown = ({ settings }) => !bHas(settings.show, Show.hideTableGroup);
function getTableGroupColors(group) {
	const background = toOpaqueHex(group.color);
	const foreground = background && contrastTextColor(background);
	return background && foreground ? {
		background,
		foreground
	} : null;
}
function getTableHeaderTint(state, table, source = "document") {
	if (source !== "document" || !isTableGroupShown(state)) return null;
	return getTableGroupTint(state, getTableGroupId(state, table), source);
}
function getTableGroupTint(state, groupId, source = "document") {
	if (!groupId || source !== "document" || !isTableGroupShown(state)) return null;
	const group = state.collections.tableGroupEntities[groupId];
	return group ? getTableGroupColors(group) : null;
}
//#endregion
export { findTableGroupsAt, getTableCenter, getTableGroupColors, getTableGroupId, getTableGroupMemberIds, getTableGroupMembers, getTableGroupRect, getTableGroupRects, getTableGroupTint, getTableGroupWraps, getTableHeaderTint, getTablesGroupRect, growTableGroupBox, isPointInRect, isTableGroupShown, nextTableGroupZIndex, padRect, seedTableGroupBoxes };
